<script setup lang="ts">
const route = useRoute();

const { $get } = useAxios();

const isTroupe = computed(() => {
  if (route.fullPath.includes("troupe")) return true;
  return false;
});

async function getActivePosters<T>(ids, limit = 3): Promise<T> {
  const now = Date.now();
  const performs = ids.reduce((result, id) => {
    return (result += `perform_in=${id}&`);
  }, "");
  return await $get(
    `/api/v3/poster?${performs}_sort=date:asc&date_gt=${new Date(
      new Date(now).setHours(new Date(now).getHours() - 1)
    ).toISOString()}&_limit=${limit}`
  );
}

const { data: state, pending: isLoaded } = await useAsyncData(
  `get state for staff by id ${route.params.id}`,
  async () => {
    const person = await $get<any>(`/api/v3/staff/${route.params.id}`);
    const performs = isTroupe.value
      ? await $get<any[]>(
          `/api/v3/perform?isActive=true&troupe.value=${route.params.id}`
        )
      : await $get<any>(
          `/api/v3/perform?isActive=true&staging.value=${route.params.id}`
        );
    const posters = await getActivePosters<any[]>(
      performs?.map((perform) => perform._id)
    );
    const photos = await $get<any[]>(
      `api/v3/photos/participant/${route.params.id}`
    );

    return { person, performs, posters, photos };
  }
);

const head = computed(() => {
  if (state.value?.person) {
    return {
      title: `${state.value?.person.name} | Государственный русский театр драмы им.Ф.А.Искандера`,
      meta: [
        {
          hid: "title",
          property: "title",
          content: `${state.value?.person.name} | Государственный русский театр драмы им.Ф.А.Искандера`,
        },
        {
          hid: "og:title",
          property: "og:title",
          content: `${state.value?.person.name} | Государственный русский театр драмы им.Ф.А.Искандера`,
        },
        {
          hid: "url",
          name: "url",
          content: `https://rusdram.org${route.fullPath}`,
        },
        {
          hid: "og:url",
          property: "og:url",
          content: `https://rusdram.org${route.fullPath}`,
        },
        {
          hid: "image",
          name: "image",
          content: `https://ucarecdn.com/${state.value?.person.img.fileKey}/-/quality/smart/image.png`,
        },
        {
          name: "og:image",
          property: "og:image",
          content: `https://ucarecdn.com/${state.value?.person.img.fileKey}/-/quality/smart/image.png`,
        },
      ],
    };
  } else {
    return {
      title: "Русдрам",
    };
  }
});

useHead(head);

const participants = computed(() => {
  let _participants = [];
  try {
    if (state.value?.photos) {
      let stringName = "";
      state.value.photos.forEach((element) => {
        stringName = "На фото: ";
        if (!element.participants.length) {
          stringName = "";
        } else {
          const lengthArray = element.participants.length - 1;
          element.participants.forEach((elem, index) => {
            if (lengthArray === index) {
              stringName += `${elem.name}.`;
            } else {
              stringName += `${elem.name}, `;
            }
          });
        }
        _participants.push(stringName);
      });
    }
  } catch (error) {}
  return _participants;
});

const participation = computed(() => {
  if (isTroupe.value) return "Играет в спектаклях";
  return "Работа над спектаклями";
});
</script>

<template>
  <nuxt-layout name="default">
    <div v-if="!isLoaded">
      <r-section v-if="state">
        <div class="flex flex-row flex-nowrap gap-x-10 relative">
          <button
            @click="$router.go(-1)"
            class="flex flex-row justify-items-center focus:outline-none"
          >
            <span class="text-light-gray text-2xl pt-1/2">
              <RIcon class="h-4 w-2" icon="angle-left" />
            </span>
            <span class="pl-4 pb-px -mt-1 text-light-gray text-xl"
              >Назад к списку</span
            >
          </button>
        </div>

        <div class="grid grid-cols-12 gap-x-7 gap-y-10 mt-8">
          <div
            class="col-start-1 col-end-13 flex flex-col lg:flex-row gap-x-7 gap-y-10 justify-stretch items-stretch"
          >
            <div class="lg:max-w-1/3">
              <img
                id="image"
                class="rounded-lg w-96"
                :src="`https://ucarecdn.com/${state.person.img.fileKey}/-/grayscale/-/quality/smart/image.png`"
                :alt="state.person.name"
              />
            </div>
            <div
              class="w-full lg:max-w-2/3 grid h-full"
              style="grid-template-rows: 80px 1fr"
            >
              <div class="h-20 col-start-1 col-end-3 flex flex-col space-y-2">
                <h1 class="text-4xl font-sans text-dark-gray">
                  {{ state.person.name }}
                </h1>
                <h6 class="font-sans text-lg2 text-primary">
                  {{ state.person.position }}
                </h6>
              </div>

              <div class="flex flex-col lg:flex-row lg:space-y-0 lg:space-x-7">
                <div
                  class="lg:max-w-1/2 flex flex-col space-y-4"
                  v-if="state.performs.length"
                >
                  <div class="justify-self-start">
                    <h4 class="text-xl font-sans text-dark-gray">
                      {{ participation }}
                    </h4>
                  </div>
                  <RDetailsTroupeAndStaff
                    :performs="state.performs"
                    :person="state.person"
                  ></RDetailsTroupeAndStaff>
                </div>

                <div
                  v-if="state.person.press.length"
                  class="flex flex-col space-y-5 lg:max-w-1/2"
                >
                  <div>
                    <h4 class="text-xl font-sans text-dark-gray">Пресса</h4>
                  </div>
                  <div class="w-full text-left flex flex-col space-y-4">
                    <template
                      v-for="item in state.person.press"
                      :key="item._id"
                    >
                      <a v-if="item" target="blank" :href="item.link">
                        <article>
                          <h5 class="text-lg font-sans text-dark-gray">
                            {{ item.name }}
                          </h5>
                          <h6 class="text-lg font-sans text-primary">
                            {{ item.author }}
                          </h6>
                        </article>
                      </a>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-start-1 col-end-13 flex flex-col space-y-4">
            <h4 class="font-sans text-xl text-gray-900">Биография</h4>
            <div class="detail" v-html="state.person.description"></div>
          </div>

          <div
            class="col-start-1 col-end-13 flex flex-col space-y-4 relative"
            v-if="state.photos.length"
          >
            <h4 class="font-sans text-xl text-gray-900">Фото</h4>
            <RSliderGallery
              :photos="state.photos"
              :participants="participants"
            ></RSliderGallery>
          </div>

          <div
            class="col-start-1 col-end-13 flex flex-col space-y-4"
            v-if="state.posters.length"
          >
            <h4 class="font-sans text-xl text-gray-900">Ближайшие спектакли</h4>
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-7">
              <RPosterCardShort
                v-for="poster in state.posters"
                :key="poster._id"
                :poster="poster"
              ></RPosterCardShort>
            </div>
          </div>
        </div>
      </r-section>
    </div>
    <r-full-page-pro-loader v-else></r-full-page-pro-loader>
  </nuxt-layout>
</template>

<style scoped>
.slide-next {
}
.detail {
  columns: 28px 2;
}

@media screen and (max-width: 1024px) {
  .detail {
    columns: 28px 1;
  }
}

.detail >>> * {
  color: #707070 !important;
}

.detail >>> a strong {
  color: #83040e !important;
}
</style>
